import { useEffect, useState } from 'react';
import { 
  Search, 
  Filter, 
  Eye, 
  Loader2,
  Package,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pending', label: 'En Attente', icon: Clock, color: 'text-yellow-600 bg-yellow-100' },
  { value: 'confirmed', label: 'Konfime', icon: CheckCircle, color: 'text-blue-600 bg-blue-100' },
  { value: 'processing', label: 'En Préparation', icon: Package, color: 'text-purple-600 bg-purple-100' },
  { value: 'shipped', label: 'Ekspedye', icon: Truck, color: 'text-indigo-600 bg-indigo-100' },
  { value: 'delivered', label: 'Livre', icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  { value: 'returned', label: 'Retounen', icon: RotateCcw, color: 'text-orange-600 bg-orange-100' },
  { value: 'cancelled', label: 'Anile', icon: XCircle, color: 'text-red-600 bg-red-100' },
];

export default function AdminOrders() {
  const { getAuthHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      setOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Erè pandan chaje kòmann');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success('Status mete ajou!');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Erè pandan mete ajou status');
    }
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#001A3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orders">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#001A3E]">Kòmann</h1>
        <p className="text-gray-500">{orders.length} kòmann total</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        {statusOptions.map((status) => {
          const count = orders.filter(o => o.status === status.value).length;
          const StatusIcon = status.icon;
          return (
            <Card 
              key={status.value} 
              className={`cursor-pointer transition-all ${statusFilter === status.value ? 'ring-2 ring-[#001A3E]' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status.value ? 'all' : status.value)}
            >
              <CardContent className="p-4 text-center">
                <StatusIcon className={`h-5 w-5 mx-auto mb-2 ${status.color.split(' ')[0]}`} />
                <p className="text-lg font-bold text-[#001A3E]">{count}</p>
                <p className="text-xs text-gray-500">{status.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Chèche pa ID oswa email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tout Status</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">ID</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 hidden sm:table-cell">Dat</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Total</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold text-gray-600">Aksyon</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredOrders.map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <p className="font-medium text-[#001A3E]">
                          #{order.id?.substring(0, 8)}
                        </p>
                        <p className="text-xs text-gray-500 sm:hidden">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="py-4 px-4 text-gray-600 hidden sm:table-cell">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-bold text-[#001A3E]">
                          ${order.total?.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Pa gen kòmann ki koresponn ak rechèch ou a
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Kòmann #{selectedOrder?.id?.substring(0, 8)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Dat</p>
                  <p className="font-medium">
                    {new Date(selectedOrder.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="font-bold text-lg text-[#001A3E]">
                    ${selectedOrder.total?.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Status Update */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Chanje Status</p>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((status) => (
                    <Button
                      key={status.value}
                      variant={selectedOrder.status === status.value ? "default" : "outline"}
                      size="sm"
                      className={selectedOrder.status === status.value ? "bg-[#001A3E]" : ""}
                      onClick={() => updateOrderStatus(selectedOrder.id, status.value)}
                    >
                      {status.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Items */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Atik yo</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">x{item.quantity}</p>
                        </div>
                      </div>
                      <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Info */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-sm text-gray-500">Metòd Peman</p>
                  <p className="font-medium">{selectedOrder.payment_method || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Status Peman</p>
                  <p className={`font-medium ${
                    selectedOrder.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {selectedOrder.payment_status === 'paid' ? 'Peye' : 'An Atant'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
